import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ReportsGroupResource, Role } from '@/types/resources';
import { api } from '@/utils/api';

import ReportsGroupForm from './ReportsGroupForm';

const Name = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  border-radius: 2.66667px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  margin-right: 16px;
  flex-shrink: 0;
  position: relative;
`;

const IconInner = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const Svg = styled.svg`
  color: ${({ theme }) => theme.colors.black};
`;

const Tr = styled.tr`
  cursor: pointer;
  > td {
    transition: 0.2s ease-out background-color;
  }
  &:hover {
    > td {
      background: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

const LockSvg = styled.svg`
  position: absolute;
  top: -6px;
  right: -6px;
`;

interface Props {
  group: ReportsGroupResource;
  userRole: Role;
}

const GroupRow: FC<Props> = ({ group, userRole }) => {
  const alerts = useAlerts();

  const [showEditForm, setShowEditForm] = useState(false);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this group?',
        group.name,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/reports-groups/${group.id}`);

      queryClient.invalidateQueries(['reports_groups']);

      alerts.success('Group was successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={() => navigate(group.id.toString())}>
      <Td>
        <Name>
          <Icon>
            {group.isRestricted && (
              <LockSvg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_6396_148668)">
                  <circle cx="7" cy="7" r="6.5" fill="white" stroke="white" />
                  <circle cx="7" cy="7" r="6" fill="#E9E9E9" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.57192 5.57502C8.57192 4.70515 7.86674 4 6.9969 4C6.12705 4 5.42188 4.70513 5.42188 5.57502V6.925H5.8719V5.57502C5.8719 4.9537 6.37555 4.45 6.99687 4.45C7.61819 4.45 8.12184 4.9537 8.12184 5.57502V6.925H8.57187L8.57192 5.57502Z"
                    fill="#222222"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.75 6.70129V8.94865C4.75 9.19792 4.95214 9.39994 5.19795 9.39994H8.80205C9.04942 9.39994 9.25 9.1981 9.25 8.94865V6.70129C9.25 6.45203 9.04786 6.25 8.80205 6.25H5.19795C4.95058 6.25 4.75 6.4518 4.75 6.70129Z"
                    fill="#222222"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6396_148668">
                    <rect width="14" height="14" fill="white" />
                  </clipPath>
                </defs>
              </LockSvg>
            )}
            <IconInner>
              <Svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.1917 9.62065V15.7509C13.1917 15.881 13.14 16.0057 13.048 16.0977C12.9561 16.1897 12.8313 16.2413 12.7013 16.2413H2.15719C2.01087 16.2413 1.87053 16.1832 1.76707 16.0797C1.6636 15.9763 1.60547 15.8359 1.60547 15.6896V7.96548C1.60547 7.81916 1.6636 7.67882 1.76707 7.57535C1.87053 7.47189 2.01087 7.41376 2.15719 7.41376H5.28363C5.40301 7.41376 5.51916 7.45248 5.61466 7.5241L7.52731 8.95859C7.62281 9.03021 7.73897 9.06893 7.85834 9.06893H12.64C12.7863 9.06893 12.9266 9.12706 13.0301 9.23053C13.1335 9.33399 13.1917 9.47433 13.1917 9.62065Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.8125 6.41381V4.75863C4.8125 4.61231 4.87063 4.47197 4.9741 4.36851C5.07756 4.26504 5.2179 4.20691 5.36422 4.20691H8.49066C8.61004 4.20691 8.72619 4.24563 8.8217 4.31725L10.7343 5.75174C10.8298 5.82336 10.946 5.86208 11.0654 5.86208H15.847C15.9933 5.86208 16.1336 5.92021 16.2371 6.02368C16.3406 6.12715 16.3987 6.26748 16.3987 6.41381V12.5441C16.3987 12.6085 16.386 12.6722 16.3614 12.7318C16.3367 12.7913 16.3006 12.8453 16.2551 12.8909C16.2095 12.9364 16.1555 12.9725 16.096 12.9972C16.0365 13.0218 15.9727 13.0345 15.9083 13.0345H14.1918"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Svg>
            </IconInner>
          </Icon>
          <Text $type="smallBold">{group.name}</Text>
        </Name>
      </Td>
      <Td>{DateTime.fromISO(group.createdAt).toLocaleString()}</Td>
      <Td>{group.reportsCount ?? 0}</Td>
      <Td>{group.note ?? '-'}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setShowEditForm(true);
              }}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditForm && (
            <ReportsGroupForm
              editableGroup={group}
              onClose={() => setShowEditForm(false)}
            />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default GroupRow;
